/**
 * Phase 19 — Investment Performance Tab
 * =====================================
 * Renders the "I already own this property" historical performance dashboard:
 *   • Header card: bought-in / years-held narrative
 *   • 6 KPI tiles (appreciation, annualized, vs S&P, equity, cash-on-cash, IRR)
 *   • Recharts line chart: property value vs S&P 500 same-dollar hypothetical
 *   • AI insight card (Emergent LLM key, cached on the deal doc)
 *
 * Data: fetched once on mount from GET /api/deals/{id}/historical-performance.
 * The backend caches the result; pressing the Refresh button re-fetches with
 * ?force=true so the user can pull the latest AVM + regenerate AI.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend,
} from "recharts";
import {
  Sparkles, RefreshCw, TrendingUp, TrendingDown, Loader2,
  LineChart as LineIcon, FileSpreadsheet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import RentHistoryModal from "@/components/deal/RentHistoryModal";

const fmtMoney = (v) => {
  if (v === null || v === undefined) return "—";
  const sign = v < 0 ? "−" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};
const fmtMoneyFull = (v) => {
  if (v === null || v === undefined) return "—";
  return `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};
const fmtPct = (v, digits = 1) => {
  if (v === null || v === undefined) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(digits)}%`;
};

function KpiTile({ label, value, sub, tone = "neutral", testid }) {
  const toneClass = {
    positive: "text-emerald-700 bg-emerald-50 border-emerald-200",
    negative: "text-rose-700 bg-rose-50 border-rose-200",
    warning: "text-amber-700 bg-amber-50 border-amber-200",
    neutral: "text-slate-900 bg-white border-slate-200",
  }[tone];
  return (
    <Card
      className={`p-5 border ${toneClass} shadow-sm transition-shadow hover:shadow-md`}
      data-testid={testid}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
        {value}
      </p>
      {sub ? (
        <p className="mt-1 text-xs text-slate-600">{sub}</p>
      ) : null}
    </Card>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      {payload.map((p) => (
        <p key={p.dataKey} className="mt-1 text-xs">
          <span
            className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
            style={{ background: p.color }}
          />
          <span className="text-slate-600">
            {p.dataKey === "property_value" ? "This property" : "S&P 500 same $"}
          </span>
          <span className="ml-2 font-semibold text-slate-900">
            {fmtMoneyFull(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

export default function PerformanceTab({ deal }) {
  const [data, setData] = useState(deal?.historical_performance || null);
  const [loading, setLoading] = useState(!deal?.historical_performance);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Phase 19.1 — actual-rent-history upload modal.
  const [rentModalOpen, setRentModalOpen] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const params = force ? { force: true } : {};
      const { data: resp } = await api.get(
        `/deals/${deal.id}/historical-performance`,
        { params },
      );
      setData(resp);
      if (force) toast.success("Performance refreshed");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e.message
        || "Failed to load performance data";
      setError(typeof msg === "string" ? msg : "Failed to load performance data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [deal.id]);

  useEffect(() => {
    if (!deal?.historical_performance) {
      load(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-16 text-slate-500"
        data-testid="performance-loading"
      >
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Crunching {Math.max(0, new Date().getFullYear() - (deal.purchase_year || 0))} years of data…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border-rose-200 bg-rose-50" data-testid="performance-error">
        <p className="text-sm font-semibold text-rose-700">Couldn't load performance</p>
        <p className="mt-1 text-sm text-rose-600">{error}</p>
        <Button onClick={() => load(false)} className="mt-3" size="sm">Retry</Button>
      </Card>
    );
  }

  if (!data) return null;

  const {
    purchase_year, current_year, years_held,
    purchase_price, current_value,
    total_appreciation, total_appreciation_pct,
    annualized_return_pct,
    sp500_compound_return_pct, sp500_delta_pct,
    equity_built, cash_on_cash_to_date_pct, irr_to_date_pct,
    current_loan_balance, principal_paid, estimated_rent_collected,
    value_over_time, ai_insight,
  } = data;

  const apprTone = total_appreciation == null ? "neutral" : total_appreciation >= 0 ? "positive" : "negative";
  const spDeltaTone = sp500_delta_pct == null ? "neutral" : sp500_delta_pct >= 0 ? "positive" : "warning";

  return (
    <div className="space-y-6" data-testid="performance-tab">
      {/* Header card — the narrative */}
      <Card
        className="overflow-hidden border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-6 shadow-sm"
        data-testid="performance-header"
      >
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">
              Investment Performance
            </p>
            <h3 className="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl" data-testid="performance-headline">
              You bought this in{" "}
              <span className="text-indigo-700">{purchase_year}</span>
              {" "}for{" "}
              <span className="text-indigo-700">{fmtMoneyFull(purchase_price)}</span>.
            </h3>
            <p className="mt-2 text-base text-slate-700">
              {years_held} year{years_held === 1 ? "" : "s"} later, it's worth{" "}
              <span className="font-semibold text-slate-900">{fmtMoneyFull(current_value)}</span>
              {total_appreciation != null ? (
                <>
                  {" "}— that's{" "}
                  <span className={total_appreciation >= 0 ? "font-semibold text-emerald-700" : "font-semibold text-rose-700"}>
                    {total_appreciation >= 0 ? "+" : "−"}{fmtMoneyFull(Math.abs(total_appreciation))}
                  </span>{" "}
                  ({fmtPct(total_appreciation_pct)}).
                </>
              ) : null}
            </p>
            {/* Phase 19 — data-quality note. When current_value == purchase_price
                it almost always means our AVM providers (RentCast + ATTOM)
                didn't have data for this address and we fell back to the
                user's own estimated_value. Without this note users mis-read
                the resulting "$0 appreciation" as a bug. */}
            {current_value && purchase_price && Math.abs(current_value - purchase_price) < 1 ? (
              <p
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200"
                data-testid="performance-avm-fallback-note"
              >
                <span className="text-amber-600">●</span>
                AVM data unavailable for this address — appreciation defaults to $0 until the property's market value is refreshed. Press <em>Refresh</em> above to retry.
              </p>
            ) : null}
          </div>
          <Button
            onClick={() => load(true)}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="shrink-0 border-indigo-200 bg-white hover:bg-indigo-50"
            data-testid="performance-refresh"
          >
            {refreshing
              ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="mr-2 h-3.5 w-3.5" />
            }
            Refresh
          </Button>
          <Button
            onClick={() => {
              // Phase 20 — Share verdict PDF (opens in a new tab). The
              // backend logs a `verdict_pdf_shared` analytics event so we
              // can measure PDF→signup attribution downstream.
              const url = `${API_BASE}/deals/${deal.id}/share-verdict-pdf?mode=performance`;
              window.open(url, "_blank", "noopener,noreferrer");
              toast.success("PDF opened in a new tab — share it with anyone");
            }}
            variant="default"
            size="sm"
            className="shrink-0 bg-indigo-600 text-white hover:bg-indigo-700"
            data-testid="performance-share-pdf"
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Share as PDF
          </Button>
        </div>
      </Card>

      {/* 6 KPI tiles */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="performance-kpis">
        <KpiTile
          label="Total appreciation"
          value={total_appreciation == null ? "—" : `${total_appreciation >= 0 ? "+" : "−"}${fmtMoney(Math.abs(total_appreciation))}`}
          sub={total_appreciation_pct == null ? null : fmtPct(total_appreciation_pct)}
          tone={apprTone}
          testid="kpi-appreciation"
        />
        <KpiTile
          label="Annualized return"
          value={fmtPct(annualized_return_pct)}
          sub="CAGR over hold period"
          tone={annualized_return_pct >= 0 ? "positive" : "negative"}
          testid="kpi-annualized"
        />
        <KpiTile
          label="vs S&P 500"
          value={fmtPct(sp500_delta_pct)}
          sub={`S&P returned ${fmtPct(sp500_compound_return_pct)} same window`}
          tone={spDeltaTone}
          testid="kpi-sp-delta"
        />
        <KpiTile
          label="Equity built"
          value={fmtMoney(equity_built)}
          sub={current_loan_balance != null ? `loan balance ${fmtMoney(current_loan_balance)}` : "cash purchase"}
          tone="neutral"
          testid="kpi-equity"
        />
        <KpiTile
          label="Cash-on-cash to date"
          value={cash_on_cash_to_date_pct == null ? "—" : fmtPct(cash_on_cash_to_date_pct)}
          sub="approx. — uses 50% rule"
          tone={cash_on_cash_to_date_pct == null || cash_on_cash_to_date_pct >= 0 ? "neutral" : "negative"}
          testid="kpi-coc"
        />
        <KpiTile
          label="IRR to date"
          value={fmtPct(irr_to_date_pct)}
          sub="appreciation-only mode"
          tone="neutral"
          testid="kpi-irr"
        />
      </div>

      {/* Chart */}
      {value_over_time && value_over_time.length > 1 ? (
        <Card className="border-slate-200 p-5 shadow-sm" data-testid="performance-chart">
          <div className="mb-3 flex items-center gap-2">
            <LineIcon className="h-4 w-4 text-slate-500" />
            <p className="text-sm font-semibold text-slate-900">
              Property value vs. S&P 500 (same starting $)
            </p>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer>
              <LineChart data={value_over_time} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="year"
                  tickLine={false}
                  axisLine={false}
                  stroke="#94a3b8"
                  fontSize={12}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  stroke="#94a3b8"
                  fontSize={12}
                  tickFormatter={(v) => fmtMoney(v)}
                  width={60}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  formatter={(value) => value === "property_value" ? "This property" : "S&P 500 same $"}
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="property_value"
                  stroke="#4f46e5"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#4f46e5" }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="sp500_equivalent"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 2.5, fill: "#10b981" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Property line is straight-line between {purchase_year} ({fmtMoneyFull(purchase_price)}) and {current_year} ({fmtMoneyFull(current_value)}).
            S&P 500 line compounds annually using historical returns. Both use the same starting dollar.
          </p>
        </Card>
      ) : null}

      {/* Extra detail strip */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="performance-secondary">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Loan balance</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{fmtMoneyFull(current_loan_balance)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Principal paid</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{fmtMoneyFull(principal_paid)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Est. rent collected</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{fmtMoneyFull(estimated_rent_collected)}</p>
          <p className="text-[10px] text-slate-500" data-testid="rent-source-label">
            {data?.rent_history_source === "actuals" ? "✓ from your actual records" : "smoothed estimate"}
          </p>
        </div>
      </div>

      {/* Phase 19.1 — actual rent history upload trigger */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
        <div className="flex items-start gap-2">
          <FileSpreadsheet className="mt-0.5 h-4 w-4 text-slate-500" />
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {data?.irr_method === "newton_with_actual_rent"
                ? "Update your rent history"
                : "Have your actual rent history?"}
            </p>
            <p className="text-xs text-slate-500">
              Replace the smoothed estimate with year-by-year actuals for a proper IRR.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setRentModalOpen(true)}
          data-testid="performance-upload-rent-history"
        >
          {data?.irr_method === "newton_with_actual_rent" ? "Edit rent history" : "Upload actual rent"}
        </Button>
      </div>

      {/* AI insight */}
      {ai_insight?.text ? (
        <Card
          className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-sm"
          data-testid="performance-ai-insight"
        >
          <div className="mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            <p className="text-sm font-semibold text-indigo-900">
              AI investor verdict
            </p>
            {ai_insight.model ? (
              <span className="ml-auto rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                {ai_insight.model.replace("-20250929", "")}
              </span>
            ) : null}
          </div>
          <p className="whitespace-pre-line text-sm leading-relaxed text-slate-800">
            {ai_insight.text}
          </p>
        </Card>
      ) : (
        <Card className="border-slate-200 bg-slate-50 p-5" data-testid="performance-ai-pending">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
            <div>
              <p className="text-sm font-semibold text-slate-700">No AI verdict yet</p>
              <p className="mt-1 text-xs text-slate-500">
                Press Refresh above to generate an investor-grade recommendation based on these numbers.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Phase 19.1 — Rent history upload modal */}
      {rentModalOpen ? (
        <RentHistoryModal
          open={rentModalOpen}
          onOpenChange={setRentModalOpen}
          deal={{ ...deal, historical_performance: data }}
          onSaved={() => load(true)}
        />
      ) : null}
    </div>
  );
}
